import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { lorisBio } from "../content/lorisBio";

const Page = styled.div`
  background: ${p => p.theme.colors.homeNavy};
  color: ${p => p.theme.colors.white};
  min-height: 100vh;
  padding: 32px 24px 64px;
  > article { max-width: 900px; margin: auto; min-width: 0; }
  a { color: ${p => p.theme.colors.accent}; text-decoration: underline; text-underline-offset: 4px; }
  a:focus-visible { outline: 2px solid ${p => p.theme.colors.homeGold}; outline-offset: 4px; }
  header {
    margin: 28px 0 32px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 315px);
    align-items: center;
    gap: 28px;
    background: radial-gradient(ellipse at 90% 65%, #104951 0%, #103a50 24%, transparent 62%);
  }
  .intro-copy { min-width: 0; }
  .intro-portrait {
    width: 100%; aspect-ratio: 1; margin: 0; overflow: hidden;
    -webkit-mask-image: linear-gradient(to right, transparent, #000 4%, #000 88%, transparent), linear-gradient(to bottom, transparent, #000 4%, #000 84%, transparent);
    -webkit-mask-composite: source-in;
    mask-image: linear-gradient(to right, transparent, #000 4%, #000 88%, transparent), linear-gradient(to bottom, transparent, #000 4%, #000 84%, transparent);
    mask-composite: intersect;
  }
  .intro-portrait img {
    display: block; width: 150%; max-width: none; height: 100%;
    /* Keep the upper face/glasses and left portrait; exclude the lower-right lettering. */
    -webkit-mask-image: linear-gradient(to bottom, #000 58%, transparent 65%), linear-gradient(to right, #000 50%, transparent 57%);
    -webkit-mask-composite: source-over;
    mask-image: linear-gradient(to bottom, #000 58%, transparent 65%), linear-gradient(to right, #000 50%, transparent 57%);
    mask-composite: add;

  }
  @media (max-width: 640px) {
    header { grid-template-columns: minmax(0, 1fr); gap: 12px; }
    .intro-portrait { width: min(270px, 100%); justify-self: center; }
  }
  header p { max-width: 68ch; font-size: 1.0625rem; line-height: 1.75; color: #c6d9e2; margin: 0 0 16px; }
  h1, h2 { font-family: "DIN Condensed", "Arial Narrow", sans-serif; font-weight: 700; color: ${p => p.theme.colors.homeGold}; text-transform: uppercase; letter-spacing: .03em; }
  h1 { font-size: clamp(2.8rem, 7vw, 4rem); line-height: 1.1; margin-bottom: 12px; }
  section { padding: clamp(24px, 4vw, 36px); margin-bottom: 24px; background: #0d293b; border: 1px solid #24495a; border-radius: 4px; }
  h3 { font-size: 1.25rem; line-height: 1.4; color: ${p => p.theme.colors.homeGold}; margin: 28px 0 14px; }
  h4 { font-family: ${p => p.theme.fonts.body}; font-size: 1.0625rem; font-weight: 500; line-height: 1.45; color: ${p => p.theme.colors.accent}; margin: 24px 0 10px; }
  nav { display: flex; flex-wrap: wrap; gap: 12px 24px; margin-bottom: 28px; }
  section { scroll-margin-top: 120px; overflow-wrap: anywhere; }
  h2 { font-size: clamp(1.65rem, 4vw, 2.2rem); line-height: 1.2; margin-bottom: 20px; }
  section p { max-width: 68ch; font-size: 1.0625rem; line-height: 1.75; color: #c6d9e2; margin: 0 0 20px; }
  section p:last-child { margin-bottom: 0; }
  section .section-caption { font-size: .8125rem; line-height: 1.5; margin: -10px 0 24px; color: ${p => p.theme.colors.accent}; }
  section .closing-note { font-size: .875rem; line-height: 1.6; margin-top: 24px; color: ${p => p.theme.colors.accent}; }
  @media (max-width: 600px) { padding: 24px 20px 48px; section { padding: 20px; } }
`;

export default function LorisHolland() {
  return <Page>
    <Helmet><title>Loris Holland — The Music Church</title><meta name="description" content="Loris Holland: professional overview and excerpts from my autobiography." /></Helmet>
    <article>
      <Link to="/about">← Back to About</Link>
      <header><div className="intro-copy"><h1>Loris Holland</h1>
        <p>If you’ve made it to this page, I’m guessing you’re curious about me. So here we go… Music has been part of my life for as long as I can remember.</p>
        </div>
        <figure className="intro-portrait">
          <img src="/images/about/loris-holland-timeshadow-profile-original.png" width="1536" height="1024" alt="Loris Holland wearing a black hat and sunglasses" />
        </figure>
      </header>
      <nav aria-label="Biography sections">{lorisBio.map(section => <a key={section.id} href={`#${section.id}`}>{section.title}</a>)}</nav>
      {lorisBio.map(section => <section id={section.id} key={section.id} aria-labelledby={`${section.id}-title`}>
        <h2 id={`${section.id}-title`}>{section.title}</h2>
        {section.caption && <p className="section-caption">{section.caption}</p>}
        {section.blocks.map((block, index) => block.type === "heading"
          ? <h3 key={index}>{block.text}</h3>
          : block.type === "subheading"
            ? <h4 key={index}>{block.text}</h4>
            : <p key={index}>{block.text.split("\n").map((line, lineIndex) => (
                <React.Fragment key={lineIndex}>{lineIndex > 0 && <br />}{line}</React.Fragment>
              ))}</p>)}
        {section.closingNote && <p className="closing-note">{section.closingNote}</p>}
      </section>)}
      <Link to="/about">← Return to About</Link>
    </article>
  </Page>;
}
